import { useState, useEffect, useCallback, useRef } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import theme from 'theme';
import 'react-perfect-scrollbar/dist/css/styles.css';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { formatDate, snapToArray, setDateHourToMidnight } from '../../utils';
import { Cell } from '../../components/Cell';
import { Portlet, PortletHeader, PortletLabel, PortletContent } from '../../components/Portlets';
import { Status } from '../../components/Status';
import { STATUS_COLORS, REQUEST_STATUS } from '../../constants/requestStatus';
import { useAuth } from '../../auth';
import { useFirestore } from '../../utils/firebase';
import { apiClient } from '../../api-client';
import { updateVacationRequestStatus } from '../../services/vacationService';
import EmployeeResolutionViewer from '../../components/EmployeeResolutionViewer/EmployeeResolutionViewer';
import { useResolutionViewer } from './useResolutionViewer';

const VacationRequestsTable = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user, getToken } = useAuth();
  const database = useFirestore();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoViewTriggeredRef = useRef(false);

  const markAccessed = useCallback(requestId => {
    setRequests(prev =>
      prev.map(request =>
        request.id === requestId
          ? {
              ...request,
              resolution: {
                ...request.resolution,
                accessLog: [...(request.resolution?.accessLog ?? []), { accessedAt: new Date() }],
              },
            }
          : request,
      ),
    );
  }, []);

  const { viewerProps, openById, openWithPaths } = useResolutionViewer({
    onAcknowledged: markAccessed,
  });

  const loadRequests = useCallback(() => {
    const q = query(
      collection(database, 'vacation_requests'),
      where('userId', '==', user.uid),
      orderBy('requestedAt', 'desc'),
    );

    return getDocs(q)
      .then(snapshot => {
        const result = snapToArray(snapshot);
        setRequests(result);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [database, user.uid]);

  useEffect(() => {
    setLoading(true);
    loadRequests();
  }, [loadRequests]);

  const handleDeleteEvent = useCallback(
    async (eventFrom, eventTo) => {
      const url = '/calendar/event';
      const body = {
        calendarType: 'vacation',
        title: 'Vacation',
        eventFrom: setDateHourToMidnight(eventFrom.toDate()),
        eventTo: setDateHourToMidnight(eventTo.toDate()),
        userId: user.uid,
        tenantId: user.tenant.id,
      };
      const response = await apiClient('DELETE', url, getToken, body);
      if (response.statusCode > 204) {
        throw response;
      }
    },
    [user, getToken],
  );

  const cancelRequest = useCallback(
    async (id, startDate, endDate) => {
      try {
        const { didUpdate, request } = await updateVacationRequestStatus(
          getToken,
          id,
          REQUEST_STATUS.CANCELED,
        );

        if (!didUpdate) {
          enqueueSnackbar('Request was already updated', { variant: 'info' });
          return;
        }

        if (request.status !== REQUEST_STATUS.PENDING) {
          try {
            await handleDeleteEvent(startDate, endDate);
          } catch (err) {
            enqueueSnackbar('Calendar event could not be removed', {
              variant: 'warning',
            });
            console.error(err);
          }
        }

        loadRequests();

        enqueueSnackbar('Vacation request canceled', {
          variant: 'success',
        });
      } catch (err) {
        enqueueSnackbar('Error when canceling vacation request', {
          variant: 'error',
        });
        console.error(err);
      }
    },
    [getToken, enqueueSnackbar, handleDeleteEvent, loadRequests],
  );

  useEffect(() => {
    const requestId = searchParams.get('requestId');
    if (!requestId || autoViewTriggeredRef.current) return;

    autoViewTriggeredRef.current = true;
    openById(requestId);
    setSearchParams(
      prev => {
        prev.delete('requestId');
        return prev;
      },
      { replace: true },
    );
  }, [searchParams, openById, setSearchParams]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hideActionsColumnAndCell = requests =>
    requests.filter(
      request =>
        request.status === 'pending' ||
        (request.vacationFrom.toDate() > new Date() && request.status === 'approved'),
    ).length > 0;

  const hasAnyResolution = requests.some(request => request.resolution?.status === 'sent');

  return (
    <Portlet
      sx={{
        background: 'none',
        border: 'none',
        boxShadow: 'none',
      }}
      outlined={1}
      squared={false}
    >
      {requests.length ? (
        <PortletHeader noDivider>
          <PortletLabel
            subtitle={`${requests.length} in total`}
            title="Latest Requests"
            sx={{
              '@media (max-width:480px)': {
                '& h5': { fontSize: '1.3rem' },
                '& h6': { marginLeft: '0.5rem', fontSize: '0.7rem', width: '3rem' },
              },
            }}
          />
        </PortletHeader>
      ) : (
        ''
      )}
      <PerfectScrollbar>
        <PortletContent
          isLoading={isLoading}
          sx={{ minWidth: '600px', overflow: 'auto' }}
          noPadding
        >
          {requests.length ? (
            <>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Requested at</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Description</TableCell>
                    {hideActionsColumnAndCell(requests) && <TableCell>Actions</TableCell>}
                    {hasAnyResolution && <TableCell>Decision on annual leave</TableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {requests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(
                      ({
                        id,
                        description,
                        noOfDays,
                        requestedAt,
                        vacationFrom,
                        vacationTo,
                        status = 'pending',
                        resolution,
                      }) => (
                        <TableRow sx={{ cursor: 'pointer' }} hover key={id} data-cy={description}>
                          <Cell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Status
                                color={STATUS_COLORS[status]}
                                size="sm"
                                sx={{ marginRight: theme.spacing(1) }}
                              />
                              {status}
                            </Box>
                          </Cell>
                          <Cell>{noOfDays}</Cell>
                          <Cell>{formatDate(requestedAt)}</Cell>
                          <Cell>{formatDate(vacationFrom)}</Cell>
                          <Cell>{formatDate(vacationTo)}</Cell>
                          <Cell>{description}</Cell>
                          {hideActionsColumnAndCell(requests) && (
                            <Cell>
                              {status === 'canceled' ||
                              status === 'denied' ||
                              (vacationFrom.toDate() <= new Date() &&
                                status === REQUEST_STATUS.APPROVED) ? null : (
                                <Button
                                  size="small"
                                  sx={{
                                    backgroundColor: theme.palette.danger.main,
                                    color: theme.palette.common.white,
                                    '&:hover': {
                                      backgroundColor: '#c62828',
                                    },
                                  }}
                                  onClick={() => cancelRequest(id, vacationFrom, vacationTo)}
                                  data-cy="cancelButton"
                                >
                                  CANCEL
                                </Button>
                              )}
                            </Cell>
                          )}
                          {hasAnyResolution && (
                            <Cell>
                              {resolution?.status === 'sent' && (
                                <Tooltip title="View document">
                                  <IconButton
                                    size="small"
                                    sx={{ p: 0 }}
                                    onClick={() =>
                                      openWithPaths(
                                        id,
                                        resolution.pdfs.map(pdf => pdf.storagePath),
                                      )
                                    }
                                  >
                                    <PictureAsPdf
                                      fontSize="small"
                                      sx={
                                        resolution.accessLog?.length > 0
                                          ? { color: 'success.main' }
                                          : undefined
                                      }
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Cell>
                          )}
                        </TableRow>
                      ),
                    )}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                component="div"
                count={requests.length}
                page={page}
              />
            </>
          ) : (
            <Box
              sx={{
                padding: '24px',
                paddingTop: 0,
                fontWeight: 300,
                color: '#777',
              }}
            >
              No vacation requests found
            </Box>
          )}
        </PortletContent>
      </PerfectScrollbar>
      <EmployeeResolutionViewer {...viewerProps} />
    </Portlet>
  );
};

export default VacationRequestsTable;
